import { useMemo } from 'react'

import {
  DxTradeAccountMetrics,
  DxTradeOpenPositionsResponse,
  DxTradeOrderStatuses,
  DxTradePosition,
} from '@model/DxTrade.types'

import { findTradeProtectiveOrders } from '../utils/protectiveOrders'
import { useDxtradeAccountMetrics } from './useDxtradeAccountMetrics'
import { useDxtradeOrders } from './useDxtradeOrders'

export type PortfolioTrade = {
  tradeId: string
  symbol: string
  side: string | null
  openedOn: string
  quantity: number
  entryPrice: number
  margin?: number
  stopLoss?: number | null
  takeProfit?: number | null
  stopLossOrderCode?: string | null
  takeProfitOrderCode?: string | null
  stopLossClientOrderId?: string | null
  takeProfitClientOrderId?: string | null
  sizeLots?: number
  lotSize?: number
  description?: string
  account?: string
  instrumentCurrency?: string | null
  priceIncrement?: number | null
}

export const useDxtradePortfolioTrades = (
  accountCode: string,
  positionsData: DxTradeOpenPositionsResponse
): {
  trades: PortfolioTrade[]
  allSymbols: string[]
  isLoading: boolean
  refreshOrders: (skipLoadingFlag?: boolean) => void
  refreshMetrics: (skipLoadingFlag?: boolean) => void
} => {
  const {
    data: metricsResp,
    isLoading: isMetLoading,
    forceRefresh: refreshMetrics,
  } = useDxtradeAccountMetrics(accountCode)
  const {
    data: ordersResp,
    isLoading: isOrdLoading,
    forceRefresh: refreshOrders,
  } = useDxtradeOrders(accountCode)

  const metricsByPosition = useMemo(() => {
    const map = new Map<string, NonNullable<DxTradeAccountMetrics['positions']>[number]>()
    const positions = metricsResp?.metrics?.[0]?.positions ?? []
    for (const metric of positions) {
      if (metric?.positionCode) map.set(metric.positionCode, metric)
    }
    return map
  }, [metricsResp])

  const orders = ordersResp?.orders || []

  const trades = useMemo<PortfolioTrade[]>(() => {
    const positionsList: DxTradePosition[] = Array.isArray(positionsData?.positions)
      ? (positionsData!.positions as DxTradePosition[])
      : []
    return positionsList.map((position) => {
      const metrics = metricsByPosition.get(position.positionCode)
      const {
        stopLoss,
        takeProfit,
        stopLossOrderCode,
        takeProfitOrderCode,
        stopLossClientOrderId,
        takeProfitClientOrderId,
      } = findTradeProtectiveOrders(position.positionCode, orders)

      return {
        tradeId: position.positionCode,
        symbol: position.symbol,
        side: position.side,
        openedOn: position.openTime,
        quantity: position.quantity,
        entryPrice: position.openPrice,
        account: position.account,
        margin: metrics?.margin,
        stopLoss,
        takeProfit,
        stopLossOrderCode,
        takeProfitOrderCode,
        stopLossClientOrderId,
        takeProfitClientOrderId,
      }
    })
  }, [positionsData, metricsByPosition, orders])

  const allSymbols = useMemo(() => {
    const positionSymbols = (positionsData?.positions || []).map((position) => position.symbol)
    const orderSymbols = orders
      .filter((order) => order.status === DxTradeOrderStatuses.WORKING)
      .map((order) => order.instrument)
      .filter(Boolean) as string[]

    const combined = [...positionSymbols, ...orderSymbols]
    const unique = Array.from(new Set(combined))

    return unique
  }, [positionsData, orders])

  return {
    trades,
    allSymbols,
    isLoading: isMetLoading || isOrdLoading,
    refreshOrders,
    refreshMetrics,
  }
}
